import type { GenericObject } from "#types";

export abstract class GAudioNode<P extends GenericObject = GenericObject> {
	abstract type: string;

	protected props: P;
	protected audioContext: AudioContext;

	#inputGainNode: AudioNode;
	#outputGainNode: AudioNode;

	constructor(audioContext: AudioContext, initialProps: P) {
		this.props = { ...initialProps };
		this.audioContext = audioContext;
		this.#inputGainNode = audioContext.createGain();
		this.#outputGainNode = audioContext.createGain();
	}

	get inNode(): AudioNode {
		return this.#inputGainNode;
	}

	get outNode(): AudioNode {
		return this.#outputGainNode;
	}

	static getWorklets(): Promise<string[]> {
		return Promise.resolve([]);
	}

	connect(node: AudioNode | GAudioNode): void {
		if (node instanceof AudioNode) {
			this.#outputGainNode.connect(node);
		} else if (node instanceof GAudioNode) {
			this.#outputGainNode.connect(node.#inputGainNode);
		} else {
			throw new TypeError("Unexpected node type, unable to connect");
		}
	}

	disconnect(node?: AudioNode | GAudioNode): void {
		if (!node) {
			this.#outputGainNode.disconnect();
		} else if (node instanceof AudioNode) {
			this.#outputGainNode.disconnect(node);
		} else if (node instanceof GAudioNode) {
			this.#outputGainNode.disconnect(node.#inputGainNode);
		} else {
			throw new TypeError("Unexpected node type, unable to disconnect");
		}
	}

	set(nextProps: Partial<P>): void {
		const previousProps = { ...this.props };

		Object.assign(this.props, nextProps);

		this.propsUpdated(this.props, previousProps);
	}

	abstract destroy(): void;

	protected abstract propsUpdated(props: P, previousProps: P): void;
}

export abstract class GInstrumentNode<
	P extends GenericObject = GenericObject,
	S extends GenericObject = GenericObject,
> extends GAudioNode<P> {
	private subscribers = new Set<GInstrumentNodeSubscriber<S>>();

	subscribe(subscriber: GInstrumentNodeSubscriber<S>): () => void {
		this.subscribers.add(subscriber);

		return () => {
			this.subscribers.delete(subscriber);
		};
	}

	protected notifySubscribers = (state: S) => {
		for (const subscriber of this.subscribers) {
			subscriber(state);
		}
	};

	abstract play(): void;

	abstract stop(): void;
}

type GInstrumentNodeSubscriber<S> = (state: S) => unknown;
